import React from 'react';
import { Button, Grid, FormLabel } from '@material-ui/core';
import { FormikAutocompleteInput, FormikDateInput, FormikTextInput } from '@webapps/reactjs-tech-components';
import { useStyles } from '@webapps/styles';
import { format } from 'date-fns';
import { ExpandMoreRounded } from '@material-ui/icons';

const TYPE = [
  { label: 'SMIC', value: 'SMIC' },
  { label: 'SMC', value: 'SMC' },
];

const ErrorMessage = ({ touched, error, message }) => {
  if (!touched || !error) return null;
  return (
    <span
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        color: '#F50057',
        fontSize: '10px',
        fontFamily: 'Roboto',
        marginTop: '4px',
      }}
    >
      {message || error}
    </span>
  );
};

const DynamicContractLengths = ({ formik, settings }) => {
  const { css, global, theme: { spacing } } = useStyles();

  // Helper function to get nested values from objects (e.g., errors, touched)
  const getNestedValue = (obj, path) => {
    const parts = path.match(/([^\[\].]+)|\[(\d+)\]/g) || [];
    let current = obj;
    for (const part of parts) {
      if (part.startsWith('[') && part.endsWith(']')) {
        const index = parseInt(part.slice(1, -1), 10);
        if (Array.isArray(current)) {
          current = current[index];
        } else {
          return null;
        }
      } else if (current && part in current) {
        current = current[part];
      } else {
        return null;
      }
    }
    return current;
  };

  // Get the next year based on the sorted array
  const getNextYear = () => {
    if (formik.values.businessContractLengths.length === 0) return 1;
    const sorted = [...formik.values.businessContractLengths].sort((a, b) => a.year - b.year);
    return sorted[sorted.length - 1].year + 1;
  };

  // Check if the last entry has an age change
  const getAgeChange = () => {
    if (formik.values.businessContractLengths.length === 0) return false;
    return formik.values.businessContractLengths[formik.values.businessContractLengths.length - 1].ageChange;
  };

  // Add a new contract length for the next year
  const handleAddBusinessContractLength = () => {
    const year = getNextYear();
    if (year > 4) return;
    formik.setFieldValue('businessContractLengths', [
      ...formik.values.businessContractLengths,
      { startDate: '', endDate: '', type: '', percentage: '', year, ageChange: false },
    ]);
  };

  // Add a new contract length with age change
  const handleAddBusinessContractLengthWithAgeChange = () => {
    const year = getNextYear();
    formik.setFieldValue('businessContractLengths', [
      ...formik.values.businessContractLengths,
      { startDate: '', endDate: '', type: '', percentage: '', year: year - 1, ageChange: true },
    ]);
  };

  // Remove the last contract length
  const handleRemoveBusinessContractLength = () => {
    if (formik.values.businessContractLengths.length > 1) {
      formik.setFieldValue('businessContractLengths', 
        formik.values.businessContractLengths.slice(0, -1)
      );
    }
  };

  // Format date for consistency
  const formatDate = (e) => format(new Date(e), "yyyy-MM-dd'T'HH:mm:ss");

  return (
    <>
      {formik.values.businessContractLengths.map((contract, index) => {
        const basePath = `businessContractLengths[${index}]`;
        return (
          <React.Fragment key={index}>
            <Grid container spacing={2} className={css({ marginTop: spacing(2) })}>
              {/* Start Date */}
              <Grid item xs={6}>
                <div style={{ position: 'relative' }}>
                  <FormLabel>
                    {`Date de début année ${contract.year} ${contract.ageChange ? "(Avec changement d'âge)" : ""}`}
                    <span style={{ color: '#0060F9', fontSize: '10px', paddingLeft: '2px', verticalAlign: 'top' }}>*</span>
                  </FormLabel>
                  <FormikDateInput
                    formik={formik}
                    name={`${basePath}.startDate`}
                    fullWidth
                    value={contract.startDate || ''}
                    onChange={(e) => {
                      formik.setFieldValue(`${basePath}.startDate`, formatDate(e))
                      if (settings?.submitType === 'onBlur') formik.submitForm();
                    }}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                    }}
                    showError={false}
                  />
                  <ErrorMessage
                    touched={getNestedValue(formik.touched, `${basePath}.startDate`)}
                    error={getNestedValue(formik.errors, `${basePath}.startDate`)}
                    message="Date de début requise"
                  />
                </div>
              </Grid>

              {/* End Date */}
              <Grid item xs={6}>
                <div style={{ position: 'relative' }}>
                  <FormLabel>
                    {`Date de fin année ${contract.year} ${contract.ageChange ? "(Avec changement d'âge)" : ""}`}
                    <span style={{ color: '#0060F9', fontSize: '10px', paddingLeft: '2px', verticalAlign: 'top' }}>*</span>
                  </FormLabel>
                  <FormikDateInput
                    formik={formik}
                    name={`${basePath}.endDate`}
                    fullWidth
                    value={contract.endDate || ''}
                    onChange={(e) => {
                      formik.setFieldValue(`${basePath}.endDate`, formatDate(e))
                      if (settings?.submitType === 'onBlur') formik.submitForm();
                    }}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      if (settings?.submitType === 'onBlur') formik.submitForm();
                    }}
                    showError={false}
                  />
                  <ErrorMessage
                    touched={getNestedValue(formik.touched, `${basePath}.endDate`)}
                    error={getNestedValue(formik.errors, `${basePath}.endDate`)}
                    message="Date de fin requise"
                  />
                </div>
              </Grid>

              {/* Type */}
              <Grid item xs={6}>
                <div style={{ position: 'relative' }}>
                  <FormLabel>
                    {`Type de salaire année ${contract.year} ${contract.ageChange ? "(Avec changement d'âge)" : ""}`}
                    <span style={{ color: '#0060F9', fontSize: '10px', paddingLeft: '2px', verticalAlign: 'top' }}>*</span>
                  </FormLabel>
                  <FormikAutocompleteInput
                    formik={formik}
                    name={`${basePath}.type`}
                    fullWidth
                    options={TYPE}
                    getOptionLabel={(option) => option.label || ''}
                    value={TYPE.find((option) => option.value === contract.type) || null}
                    onChange={(event, value) => formik.setFieldValue(`${basePath}.type`, value?.value || '')}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      if (settings?.submitType === 'onBlur') formik.submitForm();
                    }}
                    showError={false}
                    endAdornment={<ExpandMoreRounded />}
                  />
                  <ErrorMessage
                    touched={getNestedValue(formik.touched, `${basePath}.type`)}
                    error={getNestedValue(formik.errors, `${basePath}.type`)}
                    message="Type de salaire requis"
                  />
                </div>
              </Grid>

              {/* Percentage */}
              <Grid item xs={6}>
                <div style={{ position: 'relative' }}>
                  <FormLabel>
                    {`Pourcentage de salaire année ${contract.year} ${contract.ageChange ? "(Avec changement d'âge)" : ""}`}
                    <span style={{ color: '#0060F9', fontSize: '10px', paddingLeft: '2px', verticalAlign: 'top' }}>*</span>
                  </FormLabel>
                  <FormikTextInput
                    formik={formik}
                    name={`${basePath}.percentage`}
                    fullWidth
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 1 }}
                    value={contract.percentage || ''}
                    onChange={(e) => formik.setFieldValue(`${basePath}.percentage`, e.target.value)}
                    onBlur={(e) => {
                      formik.handleBlur(e);
                      if (settings?.submitType === 'onBlur') formik.submitForm();
                    }}
                    showError={false}
                  />
                  <ErrorMessage
                    touched={getNestedValue(formik.touched, `${basePath}.percentage`)}
                    error={getNestedValue(formik.errors, `${basePath}.percentage`)}
                    message="Pourcentage requis"
                  />
                </div>
              </Grid>
            </Grid>
          </React.Fragment>
        );
      })}
      <Grid item xs={12} className={css({ marginTop: spacing(3) })}>
        {getNextYear() < 5 && (
          <Button
            className={global.lightBlueButton}
            onClick={handleAddBusinessContractLength}
            variant="outlined"
            style={{ marginRight: spacing(2) }}
          >
            Nouvelle ligne
          </Button>
        )}
        {!getAgeChange() && (
          <Button
            className={global.lightBlueButton}
            onClick={handleAddBusinessContractLengthWithAgeChange}
            variant="outlined"
            style={{ marginRight: spacing(2) }}
          >
            Changement grille tarifaire
          </Button>
        )}
        {formik.values.businessContractLengths.length > 1 && (
          <Button
            className={global.lightRedButton}
            onClick={handleRemoveBusinessContractLength}
            variant="outlined"
          >
            Supprimer la dernière ligne
          </Button>
        )}
      </Grid>
    </>
  );
};

export default DynamicContractLengths;